import React from 'react';

const Section = ({ id, title, children }) => (
  <div className="settings-section" id={id}>
    <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#333' }}>{title}</h3>
    {children}
  </div>
);

const UserGuidePage = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const sections = [
    { id: 'logging-in', label: 'Logging In' },
    { id: 'switching-events', label: 'Switching Events' },
    { id: 'managing-guests', label: 'Managing Guests' },
    { id: 'sending-sms', label: 'Sending SMS Messages' },
    { id: 'sending-whatsapp', label: 'Sending WhatsApp Messages' },
    { id: 'scheduled-messages', label: 'Scheduled Messages' },
    { id: 'viewing-events', label: 'Viewing Your Events' },
    { id: 'song-requests', label: '#HeyDJ Song Requests' },
    { id: 'profile', label: 'Your Profile' },
  ];

  return (
    <div className="whatsapp-responses-page">
      <h2 className="page-title">User Guide</h2>
      <p className="page-subtitle">Everything you need to know as an Event Planner.</p>

      {/* Table of contents */}
      <div className="settings-section" style={{ paddingBottom: '8px' }}>
        <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#333' }}>Contents</h3>
        <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '2' }}>
          {sections.map(s => (
            <li key={s.id}>
              <button
                onClick={() => scrollTo(s.id)}
                style={{ background: 'none', border: 'none', color: '#7c4dbc', cursor: 'pointer', padding: 0, fontSize: '0.95rem', textDecoration: 'underline' }}
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <Section id="logging-in" title="Logging In">
        <ol style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
          <li>Navigate to the platform URL</li>
          <li>Click <strong>Login</strong> — you will be redirected to a secure login page</li>
          <li>Enter your credentials</li>
          <li>You will be returned to the home page showing your active event</li>
        </ol>
      </Section>

      <Section id="switching-events" title="Switching Events">
        <p style={{ marginTop: 0 }}>If you manage more than one event:</p>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
          <li>A dropdown appears on the <strong>Home</strong> page and <strong>Profile</strong> page showing all your events</li>
          <li>Select a different event to switch — the entire platform switches context to that event</li>
          <li>The active event name is shown in the navigation bar</li>
        </ul>
      </Section>

      <Section id="managing-guests" title="Managing Guests">
        <p style={{ marginTop: 0 }}>Navigate to <strong>Guests → List Guests</strong> to view all registered guests.</p>
        <p><strong>View guests:</strong> The list shows each guest's name, phone number, message preferences (SMS/WhatsApp), and event groups.</p>

        <p><strong>Add a guest manually:</strong></p>
        <ol style={{ paddingLeft: '20px', lineHeight: '1.8', marginTop: 0 }}>
          <li>Go to <strong>Guests → Add Guest</strong></li>
          <li>Enter the guest's name, phone number, notification preference, and group</li>
          <li>Click <strong>Add Guest</strong></li>
        </ol>

        <p><strong>Bulk import guests:</strong></p>
        <ol style={{ paddingLeft: '20px', lineHeight: '1.8', marginTop: 0 }}>
          <li>Go to <strong>Guests → Bulk Add Guests</strong></li>
          <li>Prepare a CSV or Excel file with columns: Name, Phone Number, Message Preference (SMS/WhatsApp/Both), Group (optional)</li>
          <li>Upload the file — a preview is shown before importing</li>
          <li>Click <strong>Import</strong> — results show success, failure, and duplicate counts</li>
        </ol>

        <p><strong>Edit a guest:</strong></p>
        <ol style={{ paddingLeft: '20px', lineHeight: '1.8', marginTop: 0 }}>
          <li>In the guest list, click <strong>Edit</strong> next to a guest</li>
          <li>Update name, phone, groups, or message preferences</li>
          <li>Click <strong>Save</strong></li>
        </ol>

        <p><strong>Delete a guest:</strong></p>
        <ol style={{ paddingLeft: '20px', lineHeight: '1.8', marginTop: 0 }}>
          <li>In the guest list, click <strong>Delete</strong> next to a guest</li>
          <li>Confirm the deletion</li>
        </ol>
      </Section>

      <Section id="sending-sms" title="Sending SMS Messages">
        <p style={{ marginTop: 0 }}>Navigate to <strong>Messages → SMS Message</strong>.</p>
        <ol style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
          <li><strong>Select a template</strong> from the dropdown, or choose <strong>Custom message</strong> at the bottom to write your own</li>
          <li>If using a template, fill in any variable fields (e.g., venue name, time)</li>
          <li><strong>Select recipients</strong> — choose one or more groups (All, Bride, Groom, Bridal Party, Event Staff). Recipients must also have SMS enabled.</li>
          <li>Review the <strong>message preview</strong></li>
          <li>Choose <strong>Send Now</strong> or <strong>Schedule for Later</strong>
            <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
              <li>If scheduling: enter date, time, and timezone</li>
            </ul>
          </li>
          <li>Click <strong>Send</strong> or <strong>Schedule</strong></li>
        </ol>
        <div className="input-hint" style={{ marginTop: '8px' }}>
          Messages are capped at 500 characters. The character counter turns red when approaching the limit.
        </div>
      </Section>

      <Section id="sending-whatsapp" title="Sending WhatsApp Messages">
        <p style={{ marginTop: 0 }}>Navigate to <strong>Messages → WhatsApp Message</strong>.</p>
        <ol style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
          <li><strong>Select a template</strong> (WhatsApp requires an approved template)</li>
          <li>Fill in any variable fields</li>
          <li>Select recipients and optionally schedule</li>
          <li>Click <strong>Send</strong> or <strong>Schedule</strong></li>
        </ol>
      </Section>

      <Section id="scheduled-messages" title="Managing Scheduled Messages">
        <p style={{ marginTop: 0 }}>Navigate to <strong>Messages → Scheduled Messages</strong> to view all upcoming and past scheduled messages.</p>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
          <li><strong>Filter</strong> by date range (Today &amp; Future, All, Past) and status (Pending, Sent, Failed, Cancelled)</li>
          <li><strong>Edit</strong> a pending message to change the time, recipients, or content</li>
          <li><strong>Send Now</strong> to trigger a pending message immediately (reschedules to within 1 minute)</li>
          <li><strong>Cancel</strong> a pending message to stop it from sending</li>
        </ul>
      </Section>

      <Section id="viewing-events" title="Viewing Your Events">
        <p style={{ marginTop: 0 }}>Navigate to <strong>Events</strong> to view and manage the events you have access to.</p>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
          <li>The event list shows each event's name, ID, event code, variables, assigned templates, and enabled features (AI, #HeyDJ, Media)</li>
          <li>Click <strong>Edit</strong> on any event to open the edit panel</li>
        </ul>

        <p><strong>What you can edit:</strong></p>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.8', marginTop: 0 }}>
          <li><strong>Variables</strong> — key/value pairs used in message templates (e.g., <code>bride_name: Sarah</code>, <code>venue: Grand Ballroom</code>)
            <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
              <li>Edit existing values inline in the input field next to each key</li>
              <li>Add new variables with the Key / Value fields and click <strong>Add</strong></li>
              <li>Remove variables with the <strong>X</strong> button</li>
            </ul>
          </li>
        </ul>

        <p><strong>What is read-only:</strong></p>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.8', marginTop: 0 }}>
          <li>Event Name and Event Code — managed by an administrator</li>
          <li>Features (AI, #HeyDJ, Media) — shown as checked/unchecked but cannot be toggled</li>
          <li>Assigned Templates — shown as clickable pills; click any template name to preview its message body, channel, and variable placeholders</li>
        </ul>
        <div className="input-hint" style={{ marginTop: '8px' }}>Click <strong>Save Changes</strong> to apply your variable updates.</div>
      </Section>

      <Section id="song-requests" title="#HeyDJ Song Requests">
        <p style={{ marginTop: 0 }}>Navigate to <strong>Messages → #HeyDJ</strong> to see all song requests.</p>
        <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
          <li>View all requests with guest name, channel, song text, and status</li>
          <li>Mark requests as <strong>Approved</strong>, <strong>Rejected</strong>, or leave <strong>Unmarked</strong></li>
          <li>Delete requests you no longer need</li>
          <li>Generate a <strong>public DJ link</strong> to share with the DJ</li>
          <li>Generate a <strong>guest submission QR code</strong> to display at the event</li>
        </ul>
      </Section>

      <Section id="profile" title="Your Profile">
        <p style={{ marginTop: 0 }}>Navigate to the <strong>user menu (top right) → Profile</strong> to view your account details and switch active events.</p>
      </Section>
    </div>
  );
};

export default UserGuidePage;
